import type { JSX } from 'react';
import { Footer } from '@/components/layout';
import styles from './PolicyPage.module.css';

const PolicyPage = (): JSX.Element => {
  return (
    <div className={styles.container}>
      <div className={styles.innerContainer}>
        <h1 className={styles.heading}>Privacy Policy</h1>
        <p className={styles.subHeading}>Last updated: 14th May 2025</p>
        <br />
        <div className={styles.content}>
          <p>
            At <strong>Slayd</strong>, we are committed to protecting your
            privacy. This Privacy Policy explains how we collect, use, store,
            share, and protect your personal information when you use our
            website, mobile application, or Instagram bot (collectively, the
            “Platform”). It also outlines how your data powers our AI-driven
            fashion discovery features and your rights regarding this data.
            <br />
            By using Slayd, you acknowledge that you have read and understood
            this Privacy Policy and agree to its terms. If you do not agree,
            please refrain from using the Platform.
          </p>

          {/* Section: What Information We Collect */}
          <hr />
          <p className={styles.sectionHeading}>What Information We Collect</p>

          <div className={styles.policyDescription}>
            <p className={styles.subHeading}>a) User-Provided Information</p>
            <ul>
              <li>
                <span className={styles.subHeading}>Account Data:</span> Name,
                email, and (optionally) phone number if you contact us.
              </li>
              <li>
                <span className={styles.subHeading}>Text Queries:</span> Search
                terms and natural language inputs.
              </li>
              <li>
                <span className={styles.subHeading}>Images or Reels:</span>{' '}
                Screenshots or videos shared via DM or upload for fashion
                search.
              </li>
              <li>
                <span className={styles.subHeading}>
                  Wishlist & Saved Items:
                </span>{' '}
                Any content you save for later.
              </li>
              <li>
                <span className={styles.subHeading}>Feedback & Ratings:</span>{' '}
                Inputs you provide to help us improve.
              </li>
            </ul>
          </div>

          <div className={styles.policyDescription}>
            <p className={styles.subHeading}>b) Behavioral Data</p>
            <ul>
              <li>
                <span className={styles.subHeading}>
                  Search & Interaction Logs:
                </span>{' '}
                What you browse, tap on, and how long you engage.
              </li>
              <li>
                <span className={styles.subHeading}>Clicks & Taps:</span>{' '}
                Products or carousels you interact with.
              </li>
              <li>
                <span className={styles.subHeading}>AI Engagement:</span> How
                often you use our AI stylist, how you respond to curated drops,
                etc.
              </li>
            </ul>
          </div>

          <div className={styles.policyDescription}>
            <p className={styles.subHeading}>c) Technical Data</p>
            <ul>
              <li>
                <span className={styles.subHeading}>Device Info:</span> OS,
                browser type, language, screen size, app version.
              </li>
              <li>
                <span className={styles.subHeading}>
                  Session & Cookie Data:
                </span>{' '}
                Products or carousels you interact with.
              </li>
              <li>
                <span className={styles.subHeading}>
                  Crash Reports & Diagnostics:
                </span>{' '}
                For debugging and improving user experience.
              </li>
              <li>
                <span className={styles.subHeading}>Location (optional):</span>{' '}
                If enabled, to show nearby fashion options or relevant
                collections.
              </li>
            </ul>
          </div>

          {/* Section: How We Use Your Information */}
          <hr />
          <p className={styles.sectionHeading}>How We Use Your Information</p>
          <p>We use your information to:</p>
          <ul>
            <li>
              <span className={styles.subHeading}>Personalize Discovery:</span>{' '}
              Show recommendations based on what you like, ask, and explore.
            </li>
            <li>
              <span className={styles.subHeading}>Power Our AI Models:</span>{' '}
              Train and refine the AI that suggests looks, curates moodboards,
              and understands text queries.
            </li>
            <li>
              <span className={styles.subHeading}>Improve Features:</span>{' '}
              Analyze usage patterns to improve our UI, AI accuracy, and feature
              flow.
            </li>
            <li>
              <span className={styles.subHeading}>Communicate With You:</span>{' '}
              Send you links, curated looks, or support responses via DM or
              email.
            </li>
            <li>
              <span className={styles.subHeading}>
                Ensure Platform Security:
              </span>{' '}
              Detect and prevent fraud or misuse of the service.
            </li>
            <li>
              <span className={styles.subHeading}>Support Order Journeys:</span>{' '}
              Help track or resolve questions about products you explore through
              Slayd.
            </li>
          </ul>
          <p>
            We do not use your data for biometric profiling or facial
            recognition.
          </p>

          {/* Section: Cookies */}
          <hr />
          <p className={styles.sectionHeading}>
            Use of Cookies & Tracking Technologies
          </p>
          <p>We use cookies, local storage, and tracking pixels to:</p>
          <ul>
            <li>
              <span className={styles.subHeading}>Keep you logged in:</span>{' '}
              Maintain session continuity while using the platform.
            </li>
            <li>
              <span className={styles.subHeading}>Measure feature usage:</span>{' '}
              Track which features and products are most popular.
            </li>
            <li>
              <span className={styles.subHeading}>
                Personalize your experience:
              </span>{' '}
              Tailor your homepage and alerts to your preferences.
            </li>
            <li>
              <span className={styles.subHeading}>Monitor performance:</span>{' '}
              Measure system performance, load times, and technical issues.
            </li>
          </ul>
          <p>
            You can control cookies via your browser or device settings.
            Disabling certain cookies may affect your experience.
          </p>

          {/* Section: Sharing of Your Data */}
          <hr />
          <p className={styles.sectionHeading}>Sharing of Your Data</p>
          <p>Slayd only shares your data when necessary:</p>
          <ul>
            <li>
              <span className={styles.subHeading}>
                Cloud & AI Infra Providers:
              </span>{' '}
              For hosting and AI processing.
            </li>
            <li>
              <span className={styles.subHeading}>
                Checkout Partners (e.g., Shiprocket):
              </span>{' '}
              If you check out using an integrated cart.
            </li>
            <li>
              <span className={styles.subHeading}>
                Retailer Affiliate Links:
              </span>{' '}
              To track engagement and earn commissions.
            </li>
            <li>
              <span className={styles.subHeading}>Legal Authorities:</span> Only
              if required by law or court order.
            </li>
            <li>
              <span className={styles.subHeading}>Business Transfers:</span> If
              we are acquired or restructured, your data may transfer with the
              company.
            </li>
          </ul>
          <p>We do not sell your data to third-party marketers.</p>

          {/* Other Sections */}
          <hr />
          <p className={styles.sectionHeading}>Checkout Information</p>
          <p>
            Slayd does not collect payment data. If you use a checkout powered
            by a partner like Shiprocket, your order details (excluding payment
            info) may be shared with Slayd to help with tracking or support.
          </p>

          <hr />
          <p className={styles.sectionHeading}>{`Children's Privacy`}</p>
          <p>
            Slayd is not meant for individuals under 18. We do not knowingly
            collect data from minors without parental consent. If we learn we’ve
            collected such data, we’ll delete it promptly.
          </p>

          <hr />
          <p className={styles.sectionHeading}>Data Security</p>
          <p>We implement strong safeguards to protect your data:</p>
          <ul>
            <li>
              <span className={styles.subHeading}>End-to-end encryption:</span>{' '}
              For data in transit.
            </li>
            <li>
              <span className={styles.subHeading}>
                Secure server infrastructure:
              </span>{' '}
              All user data is stored on protected environments.
            </li>
            <li>
              <span className={styles.subHeading}>Limited access:</span> Only
              authorized personnel may access user data.
            </li>
            <li>
              <span className={styles.subHeading}>Vulnerability testing:</span>{' '}
              We regularly test and patch the system for security flaws.
            </li>
          </ul>
          <p>
            Still, no digital system is 100% immune — we recommend you also take
            basic precautions with your credentials.
          </p>

          <hr />
          <p className={styles.sectionHeading}>Data Retention</p>
          <p>We keep:</p>
          <ul>
            <li>
              <span className={styles.subHeading}>User interaction data:</span>{' '}
              As long as your account or DMs remain active.
            </li>
            <li>
              <span className={styles.subHeading}>AI training data:</span> Up to
              2 years in anonymized or pseudonymized form.
            </li>
            <li>
              <span className={styles.subHeading}>
                Checkout or support data:
              </span>{' '}
              Until resolution or for analytics.
            </li>
            <li>
              <span className={styles.subHeading}>Deletion/anonymization:</span>{' '}
              Upon your request or after inactivity, we delete or anonymize your
              data in line with applicable laws.
            </li>
          </ul>

          <hr />
          <p className={styles.sectionHeading}>Cross-Border Data Transfers</p>
          <p>
            Slayd uses global service providers (e.g., cloud infrastructure, AI
            APIs), which may process data outside India. These transfers are
            conducted with safeguards and in compliance with DPDP regulations.
          </p>

          <hr />
          <p className={styles.sectionHeading}>Links to Other Sites</p>
          <p>
            If you’re redirected to a third-party retailer (e.g., Myntra or
            Urbanic), their privacy policy governs that transaction. Please
            review their terms before entering payment or personal details.
          </p>

          <hr />
          <p className={styles.sectionHeading}>Your Rights</p>
          <p>
            Under India’s Digital Personal Data Protection Act (DPDP), 2023, you
            have the right to:
          </p>
          <ul>
            <li>
              <span className={styles.subHeading}>Access your data</span>
            </li>
            <li>
              <span className={styles.subHeading}>
                Request correction or deletion
              </span>
            </li>
            <li>
              <span className={styles.subHeading}>Withdraw consent</span> for
              image uploads, contacts, or location use
            </li>
            <li>
              <span className={styles.subHeading}>Lodge complaints</span> about
              data handling
            </li>
          </ul>
          <p>You can contact us to exercise any of these rights.</p>

          <hr />
          <p className={styles.sectionHeading}>Consent</p>
          <p>By using Slayd, you consent to:</p>
          <ul>
            <li>
              <span className={styles.subHeading}>
                Collection and use of your data:
              </span>{' '}
              As described in this policy.
            </li>
            <li>
              <span className={styles.subHeading}>Optional data:</span> Use of
              image uploads, location data, or contacts (if enabled).
            </li>
            <li>
              <span className={styles.subHeading}>Communication:</span>{' '}
              Receiving occasional emails or DMs (with opt-out options
              available).
            </li>
          </ul>
          <p>
            You may withdraw consent at any time. Doing so may affect some
            platform features.
          </p>

          <hr />
          <p className={styles.sectionHeading}>Changes to This Policy</p>
          <p>
            We may update this policy from time to time. Major changes will be
            communicated via our app or DM. Continued use after changes implies
            consent to the updated policy.
          </p>

          <hr />
          <p className={styles.sectionHeading}>Contact Us</p>
          <p>
            If you have questions, requests, or privacy concerns, feel free to
            reach out to us:
          </p>
          <ul>
            <li>
              <span className={styles.subHeading}>Email:</span>{' '}
              <a href="mailto:hello@slayd.in">hello@slayd.in</a>
            </li>
            <li>
              <span className={styles.subHeading}>Phone:</span>{' '}
              <a href="tel:+917302202036">+91 7302202036</a>
            </li>
            <li>
              <span className={styles.subHeading}>Address:</span>
              <br />
              Slayd Technologies Private Limited
              <br />
              74-Pushpanjali Vatika, Avas Vikas Colony,
              <br />
              Sadar, Agra, 282007, Uttar Pradesh
            </li>
          </ul>
        </div>
      </div>
      <div className={styles.footer}>
        <Footer />
      </div>
    </div>
  );
};

export default PolicyPage;
